/**
 * #4619 Slice 3 — THE GENERIC ANSWER-CONTEXT COMPILER (shadow mode).
 *
 * Given the turn's resolved semantic selection (#4619 Slice 2) and the governed
 * numeric-binding pool available at the narration seam, this module compiles the
 * contract-admitted answer context: which governed facts the declared
 * `ExecutiveAnswerContract` ADMITS, which obligations no governed binding
 * satisfies (named gaps), which of today's payload bindings nothing admits
 * (excluded facts), and which admissions the contract's prohibited-substitution
 * table forbids. Pure function, no I/O, no model call.
 *
 * ADMISSION FORMULA (epic-verbatim). An answer may carry:
 *   1. required subject metrics             — the obligations' governed metricKeys;
 *   2. direct calculation dependencies      — governed registry composition edges
 *                                             (`composedFrom.inputMetricKeys`,
 *                                             ratio `components`) of the REQUIRED
 *                                             subject metrics, one hop, never
 *                                             transitive;
 *   3. benchmark/target dependencies        — contract-declared only (see the
 *                                             named gap below);
 *   4. specifically permitted explanatory facts — the structured
 *                                             `permittedSupportingRoles`;
 *   5. decision facts                       — ONLY when `recommendationPolicy`
 *                                             allows.
 * Nothing else is admissible. Admission is CLOSED-WORLD over the contract —
 * never over raw-question predicates (Standing Law 16), and never by top-N
 * truncation: every binding an admission clause reaches is admitted, however
 * many there are.
 *
 * SHADOW-MODE LAW. Compilation DIAGNOSES; it never erases governed computed
 * work. An excluded fact is NAMED in the artifact, not removed from any payload;
 * a prohibited substitution is RECORDED, and the offending binding stays
 * admitted under whichever clause legitimately declares its role; a failed
 * obligation becomes a named gap, never a reason to drop what did compute. No
 * output of this compiler reaches the narrator, the payload, or the stream in
 * this slice — the seam records counters only.
 *
 * NAMED GAPS IN THE GOVERNED RELATIONSHIP ESTATE (honest scouting, for Slice 4):
 *
 *   - BENCHMARK/TARGET DEPENDENCY EDGES DO NOT EXIST AS GOVERNED DATA. The
 *     governed metric registry (`packages/lib/src/metric-registry.ts`) carries
 *     only a boolean `benchmarkable` flag and the target-metric registry
 *     (`ask-helix/contracts/target-metric-registry.ts`) carries family/direction
 *     metadata — neither declares a "metric K's benchmark is metric B" /
 *     "metric K's target is metric T" metric-key adjacency. Category 3 of the
 *     admission formula therefore admits ONLY contract-declared keys (obligation
 *     `metricKeys`, `permittedSupportingRoles[].metricKeyRefs` under
 *     `benchmark_dependency`/`target_dependency`). Inventing an adjacency table
 *     here would be the `if (metricKey === ...)` anti-pattern the epic forbids.
 *
 *   - BINDINGS CARRY NO SEMANTIC-ROLE METADATA. `AskHelixNumericBindingDto`
 *     declares claimDomain, provenance, reconciliation — but no
 *     `SemanticNumberRole`. A binding's role is therefore known ONLY where the
 *     contract declares it (an obligation's `admissibleBinding.semanticRole`
 *     pinned to `metricKeys`, or a `permittedSupportingRoles[]` entry pinned to
 *     `metricKeyRefs`). The prohibited-substitution check operates exactly
 *     there and nowhere else — no role inference from placeholder names. A
 *     role-only clause (no keys) is unevaluable against a role-less pool and is
 *     surfaced as a clause diagnostic, not silently satisfied.
 *
 *   - BINDINGS CARRY NO `metricKey` FIELD. A binding's identity is its
 *     `placeholder`, authored in the same vocabulary as manifest metric keys.
 *     Matching reuses the #4008 FIX-3 convention from
 *     `apps/api/src/ask-helix/ask-helix.answer-focus-guard.ts`
 *     (`metricKeyIsBoundInLead`): exact placeholder, `.key`/`:key` namespace
 *     suffix, `key__` variant prefix, with `valuePath` as the secondary route.
 *
 *   - SCOPE-CONSTRAINT GRAINS ARE PARTIALLY EVALUABLE. A constraint's
 *     `entityRef` checks against the binding's `scope.entityId` (and against the
 *     owning module id for `grain: 'module'`); a grain WITHOUT an entityRef has
 *     no binding-side counterpart and is surfaced as a clause diagnostic.
 */
import {
  PROHIBITED_ROLE_SUBSTITUTIONS,
  SEMANTIC_NUMBER_ROLES,
  type ExecutiveAnswerContract,
  type ExecutiveAnswerFactRelationshipType,
  type ExecutiveAnswerObligation,
  type ExecutiveAnswerObligationRole,
  type ExecutiveAnswerPermittedSupportingRole,
  type ExecutiveAnswerScopeConstraint,
  type SemanticNumberRole,
} from './executive-answer-contract';
import type { AskHelixSemanticSelection } from './semantic-selection';
// #4619 Slice 4 — the ONE canonical pool walk + ref grammar, shared with the
// central bindingRef resolver so a ref minted here always resolves there.
import {
  answerContextBindingRefForPoolIndex,
  walkAnswerContextBindingPool,
} from './answer-context-binding-ref';
import { resolveRegisteredMetricAcrossCompiledVersions } from '../../metric-registry';

/**
 * The structural slice of a governed numeric binding the compiler reads.
 * `NumericBinding` (and every module-result binding) is assignable; keeping the
 * shape minimal keeps the compiler decoupled from the full DTO the way the
 * narration contracts stay decoupled from the manifest module.
 */
export type AnswerContextCandidateBinding = {
  readonly placeholder?: string | null;
  readonly valuePath?: string | null;
  readonly scope?: { readonly entityId?: string | null } | null;
};

/** One module's contribution to the narration-seam binding pool. */
export type AnswerContextBindingPoolModule = {
  readonly moduleId?: string | null;
  readonly numericBindings?: readonly AnswerContextCandidateBinding[] | null;
};

/** Governed relationship metadata for ONE metric key (see the builder below). */
export type AnswerContextMetricRelationship = {
  readonly directCalculationDependencyKeys: readonly string[];
};

/** Governed relationship metadata, keyed by governed metric key. */
export type AnswerContextMetricRelationships = Readonly<
  Record<string, AnswerContextMetricRelationship>
>;

/** Which admission-formula clause admitted a fact. Closed vocabulary. */
export const ANSWER_CONTEXT_ADMISSION_CHANNELS = Object.freeze([
  'required_subject_metric',
  'optional_subject_metric',
  'direct_calculation_dependency',
  'permitted_supporting_role',
  'decision_obligation',
  'decision_support_role',
] as const);

export type AnswerContextAdmissionChannel =
  (typeof ANSWER_CONTEXT_ADMISSION_CHANNELS)[number];

/**
 * ONE clause admission of a binding (#4619 review 5309466440 finding 1):
 * clause coverage and fact dedupe are SEPARATE concerns. Every clause that
 * legitimately admits a binding records an admission; the canonical fact
 * carries them ALL, so a later required obligation is never demoted by an
 * earlier optional admission and an outbound validator can prove clause
 * coverage from the artifact alone. The artifact is invariant to obligation
 * declaration order by construction.
 */
export type AnswerContextAdmission = {
  readonly channel: AnswerContextAdmissionChannel;
  /** Clause identity: an obligationId, `permittedSupportingRoles[i]`, or
   * `dependency_of:<subjectMetricKey>`. */
  readonly admittedBy: string;
  /** The contract/relationship metric key the binding matched. */
  readonly metricKey: string;
  /** Dependency channel only: the required subject metric this depends on. */
  readonly subjectMetricKey?: string;
  /** Supporting-role channels only: the declared relationship to the subject. */
  readonly relationshipType?: ExecutiveAnswerFactRelationshipType;
};

/**
 * The authoritative required-obligation inventory (#4619 Slice 5 repair,
 * Codex 5317276544 finding 1): a direct projection of the resolved contract's
 * `required: true` obligations, captured at compile entry BEFORE any clause
 * evaluation, so a defect in the admission walk can never drop a required
 * obligation from the artifact's own record. The outbound validator iterates
 * THIS inventory — never the compiler's admission output — when proving
 * "every required obligation is answered or honestly qualified". Minimal by
 * design: identity plus the metadata a diagnostic needs; no reparse of the
 * question and no second contract interpretation.
 */
export type AnswerContextRequiredObligation = {
  readonly obligationId: string;
  readonly role: ExecutiveAnswerObligationRole;
  readonly metricKeys: readonly string[];
};

/**
 * One CANONICAL admitted governed fact: exactly one source binding, however
 * many clauses admitted it. `bindingRef` (#4619 review 5309466440 finding 2)
 * is the opaque, exact, per-turn identity of the underlying binding — it maps
 * back to exactly one entry of this turn's binding pool (the deterministic
 * moduleResults × numericBindings walk), so two bindings sharing a placeholder
 * but differing in scope, period, value path, evidence, or value remain
 * distinguishable after compilation. Consumers resolve it against the same
 * turn's pool; they never parse prose or first-match by placeholder.
 */
export type AnswerContextFact = {
  readonly bindingRef: string;
  readonly placeholder: string;
  readonly moduleId: string | null;
  /** Every clause admission, in clause-evaluation order; never collapsed. */
  readonly admissions: readonly AnswerContextAdmission[];
};

export const ANSWER_CONTEXT_NAMED_GAP_REASONS = Object.freeze([
  'no_governed_binding_for_metric_keys',
  'admissible_binding_unevaluable_no_binding_role_metadata',
  'decision_facts_not_permitted_by_recommendation_policy',
] as const);

export type AnswerContextNamedGapReason =
  (typeof ANSWER_CONTEXT_NAMED_GAP_REASONS)[number];

/** An obligation no governed binding satisfies this turn — named, never silent. */
export type AnswerContextNamedGap = {
  readonly obligationId: string;
  readonly role: ExecutiveAnswerObligationRole;
  readonly reason: AnswerContextNamedGapReason;
  readonly metricKeys: readonly string[];
  /** The contract's honest-gap statement, when the module authored one. */
  readonly permittedGapStatement: string | null;
};

/** A pool binding present in today's payload that NO admission clause reaches. */
export type AnswerContextExcludedFact = {
  readonly bindingRef: string;
  readonly placeholder: string;
  readonly moduleId: string | null;
};

/** A detected forbidden role substitution, per the contract's closed table. */
export type AnswerContextProhibitedSubstitution = {
  readonly bindingRef: string;
  readonly placeholder: string;
  readonly metricKey: string;
  readonly obligationId: string;
  readonly offeredRole: SemanticNumberRole;
  readonly claimedRole: SemanticNumberRole;
  readonly defectClass: string;
  /** The clause that declares the binding's actual role. */
  readonly offeredRoleDeclaredBy: string;
};

export const ANSWER_CONTEXT_CLAUSE_DIAGNOSTIC_KINDS = Object.freeze([
  'admissible_binding_without_metric_keys_no_binding_role_metadata',
  'supporting_role_without_metric_key_refs_no_binding_role_metadata',
  'scope_constraint_grain_unevaluable',
  'decision_support_blocked_by_recommendation_policy',
] as const);

export type AnswerContextClauseDiagnosticKind =
  (typeof ANSWER_CONTEXT_CLAUSE_DIAGNOSTIC_KINDS)[number];

/**
 * A contract clause the compiler could not discharge against today's governed
 * metadata — diagnosed by name (the shadow-mode law), never silently skipped.
 */
export type AnswerContextClauseDiagnostic = {
  readonly kind: AnswerContextClauseDiagnosticKind;
  readonly clauseRef: string;
};

/** The typed compilation artifact — the whole output, counters derivable. */
export type AnswerContextCompilation = {
  readonly capabilityId: string;
  readonly intentId: string;
  readonly contractVersion: string;
  /** The contract's own required obligations, verbatim — see the type doc. */
  readonly requiredObligations: readonly AnswerContextRequiredObligation[];
  readonly requiredFacts: readonly AnswerContextFact[];
  readonly supportingFacts: readonly AnswerContextFact[];
  readonly permittedDecisionFacts: readonly AnswerContextFact[];
  readonly namedGaps: readonly AnswerContextNamedGap[];
  readonly excludedFacts: readonly AnswerContextExcludedFact[];
  readonly prohibitedSubstitutions: readonly AnswerContextProhibitedSubstitution[];
  readonly clauseDiagnostics: readonly AnswerContextClauseDiagnostic[];
  /** Size of today's payload binding pool (every placeholder-bearing binding). */
  readonly payloadBindingCount: number;
};

/** The four shadow counters the seam records. Items, never chars. */
export type AnswerContextCompilationCounters = {
  readonly admittedFacts: number;
  readonly excludedFacts: number;
  readonly namedGaps: number;
  readonly payloadBindings: number;
};

export function answerContextCompilationCounters(
  compilation: AnswerContextCompilation,
): AnswerContextCompilationCounters {
  return {
    admittedFacts:
      compilation.requiredFacts.length +
      compilation.supportingFacts.length +
      compilation.permittedDecisionFacts.length,
    excludedFacts: compilation.excludedFacts.length,
    namedGaps: compilation.namedGaps.length,
    payloadBindings: compilation.payloadBindingCount,
  };
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function trimmedKeys(keys: readonly string[] | undefined): string[] {
  return (Array.isArray(keys) ? keys : [])
    .map((key) => safeString(key).trim())
    .filter(Boolean);
}

function isSemanticNumberRole(value: unknown): value is SemanticNumberRole {
  return (
    typeof value === 'string' &&
    (SEMANTIC_NUMBER_ROLES as readonly string[]).includes(value)
  );
}

/** One pool entry: a placeholder-bearing binding plus its owning module. */
type PoolEntry = {
  readonly index: number;
  readonly placeholder: string;
  readonly placeholderLower: string;
  readonly valuePathLower: string;
  readonly moduleId: string | null;
  readonly scopeEntityId: string | null;
};

/**
 * The opaque per-turn binding identity (review 5309466440 finding 2): the
 * entry's position in this turn's deterministic pool walk. It maps back to
 * exactly one source binding of the same turn's moduleResults — consumers
 * resolve it through `resolveAnswerContextBindingRef` (#4619 Slice 4, the ONE
 * centralized resolver); they never re-walk the pool themselves, never parse
 * the ref or prose, and never first-match by placeholder text.
 */
function bindingRefForEntry(entry: PoolEntry): string {
  return answerContextBindingRefForPoolIndex(entry.index);
}

/**
 * The #4008 FIX-3 placeholder↔metric-key convention (`metricKeyIsBoundInLead`,
 * ask-helix.answer-focus-guard.ts), verbatim: bindings carry no metricKey field,
 * the placeholder IS the identity, `valuePath` is the secondary route.
 */
function entryMatchesMetricKey(entry: PoolEntry, metricKey: string): boolean {
  const target = metricKey.trim().toLowerCase();
  if (!target) return false;
  // A governed computation address is the binding's authoritative referent.
  // Placeholder identity is only the compatibility fallback for legacy
  // bindings without an address, so a reused display token cannot satisfy a
  // contract for a different metric.
  const valuePath = entry.valuePathLower;
  if (valuePath) {
    return valuePath === target || valuePath.endsWith(`.${target}`);
  }
  const name = entry.placeholderLower;
  if (name === target) return true;
  if (name.endsWith(`.${target}`) || name.endsWith(`:${target}`)) return true;
  if (name.startsWith(`${target}__`)) return true;
  return false;
}

function entryMatchesBindingPlaceholderRefs(
  entry: PoolEntry,
  placeholderRefs: readonly string[]
): boolean {
  if (placeholderRefs.length === 0) return true;
  return placeholderRefs.some((ref) => ref.trim().toLowerCase() === entry.placeholderLower);
}

/** A role the contract declares for bindings matching a metric key. */
type DeclaredRole = {
  readonly role: SemanticNumberRole;
  readonly declaredBy: string;
  readonly metricKey: string;
  readonly bindingPlaceholderRefs: readonly string[];
};

function collectPool(
  moduleResults: readonly AnswerContextBindingPoolModule[],
): PoolEntry[] {
  // #4619 Slice 4 — built ON the canonical shared walk (answer-context-
  // binding-ref.ts), so the `pool:<index>` refs this compiler mints are
  // defined against exactly the sequence the central resolver re-walks.
  return walkAnswerContextBindingPool(moduleResults ?? []).map((entry) => ({
    index: entry.index,
    placeholder: entry.placeholder,
    placeholderLower: entry.placeholder.toLowerCase(),
    valuePathLower: safeString(entry.binding?.valuePath).trim().toLowerCase(),
    moduleId: entry.moduleId,
    scopeEntityId: safeString(entry.binding?.scope?.entityId).trim() || null,
  }));
}

/**
 * Every role the contract itself declares, per metric key: obligations that pin
 * `admissibleBinding.semanticRole` to `metricKeys`, and supporting-role entries
 * that pin `role` to `metricKeyRefs`. This map is the ONLY source of a
 * binding's role — see the no-role-metadata named gap in the module docstring.
 */
function collectDeclaredRoles(
  contract: ExecutiveAnswerContract,
): Map<string, DeclaredRole[]> {
  const declared = new Map<string, DeclaredRole[]>();
  const add = (
    metricKey: string,
    role: SemanticNumberRole,
    declaredBy: string,
    bindingPlaceholderRefs: readonly string[]
  ) => {
    const keyLower = metricKey.toLowerCase();
    const existing = declared.get(keyLower) ?? [];
    existing.push({ role, declaredBy, metricKey, bindingPlaceholderRefs });
    declared.set(keyLower, existing);
  };
  for (const obligation of contract.obligations ?? []) {
    const role = obligation?.admissibleBinding?.semanticRole;
    if (!isSemanticNumberRole(role)) continue;
    for (const key of trimmedKeys(obligation.metricKeys)) {
      add(
        key,
        role,
        `obligation:${safeString(obligation.obligationId).trim()}`,
        trimmedKeys(obligation.bindingPlaceholderRefs)
      );
    }
  }
  (contract.permittedSupportingRoles ?? []).forEach((entry, index) => {
    const role = entry?.role;
    if (!isSemanticNumberRole(role)) return;
    for (const key of trimmedKeys(entry.metricKeyRefs)) {
      add(
        key,
        role,
        `permittedSupportingRoles[${index}]`,
        trimmedKeys(entry.bindingPlaceholderRefs)
      );
    }
  });
  return declared;
}

/** Declared roles applying to a pool entry via any contract key it matches. */
function rolesOfferedByEntry(
  entry: PoolEntry,
  declaredRoles: ReadonlyMap<string, DeclaredRole[]>,
): DeclaredRole[] {
  const offered: DeclaredRole[] = [];
  for (const roles of declaredRoles.values()) {
    for (const declared of roles) {
      if (
        entryMatchesMetricKey(entry, declared.metricKey) &&
        entryMatchesBindingPlaceholderRefs(entry, declared.bindingPlaceholderRefs)
      ) {
        offered.push(declared);
      }
    }
  }
  return offered;
}

/**
 * When every contract clause that explicitly declares a metric also pins its
 * public binding identity, carry that same exact pin into dependency admission.
 * A single unpinned declaration deliberately keeps the legacy broad behavior.
 */
function bindingPlaceholderRefsForDeclaredMetric(
  contract: ExecutiveAnswerContract,
  metricKey: string
): string[] {
  const target = metricKey.trim().toLowerCase();
  const declarations = [
    ...(contract.obligations ?? [])
      .filter((obligation) =>
        trimmedKeys(obligation.metricKeys).some((key) => key.toLowerCase() === target)
      )
      .map((obligation) => trimmedKeys(obligation.bindingPlaceholderRefs)),
    ...(contract.permittedSupportingRoles ?? [])
      .filter((role) => trimmedKeys(role.metricKeyRefs).some((key) => key.toLowerCase() === target))
      .map((role) => trimmedKeys(role.bindingPlaceholderRefs)),
  ];
  if (declarations.length === 0 || declarations.some((refs) => refs.length === 0)) {
    return [];
  }
  return Array.from(new Set(declarations.flat()));
}

function prohibitedDefectClass(
  offeredRole: SemanticNumberRole,
  claimedRole: SemanticNumberRole,
): string | null {
  const pair = PROHIBITED_ROLE_SUBSTITUTIONS.find(
    (candidate) =>
      candidate.offeredRole === offeredRole && candidate.claimedRole === claimedRole,
  );
  return pair ? pair.defectClass : null;
}

/**
 * Scope-constraint evaluation against binding metadata. Returns 'match' /
 * 'no_match' when evaluable, 'unevaluable' when the constraint has no
 * binding-side counterpart (grain without entityRef — see module docstring).
 */
function scopeConstraintVerdict(
  constraint: ExecutiveAnswerScopeConstraint | undefined,
  entry: PoolEntry,
): 'match' | 'no_match' | 'unevaluable' {
  if (!constraint) return 'match';
  const entityRef = safeString(constraint.entityRef).trim();
  if (!entityRef) return 'unevaluable';
  if (entry.scopeEntityId === entityRef) return 'match';
  if (constraint.grain === 'module' && entry.moduleId === entityRef) return 'match';
  return 'no_match';
}

export type AnswerContextCompilerInput = {
  /** The resolved selection (#4619 Slice 2). No contract ⇒ no compilation. */
  readonly selection: AskHelixSemanticSelection | null | undefined;
  /** The governed module results whose bindings form today's payload pool. */
  readonly moduleResults: readonly AnswerContextBindingPoolModule[];
  /**
   * Governed relationship metadata for the dependency closure. Absent ⇒ the
   * closure uses obligation metricKeys + contract-declared supporting roles
   * only (the honest floor when no governed dependency source exists).
   */
  readonly relationships?: AnswerContextMetricRelationships | null;
};

/**
 * Compile the contract-admitted answer context, or `null` when the turn
 * resolved no selection or the selected intent declares no contract — the
 * no-contract state compiles NOTHING rather than inventing an admission.
 */
export function compileAnswerContext(
  input: AnswerContextCompilerInput,
): AnswerContextCompilation | null {
  const selection = input.selection;
  const contract = selection?.executiveAnswerContract;
  if (!selection || !contract) return null;

  // Codex 5317276544 finding 1: the authoritative inventory is projected
  // straight off the resolved contract BEFORE any clause evaluation, so the
  // admission walk below can never drop a required obligation from the
  // artifact's record — the outbound validator compares against THIS.
  const requiredObligations: AnswerContextRequiredObligation[] = (
    contract.obligations ?? []
  )
    .filter((obligation) => obligation?.required === true)
    .map((obligation) => ({
      obligationId: safeString(obligation.obligationId).trim(),
      role: obligation.role,
      metricKeys: trimmedKeys(obligation.metricKeys),
    }));

  const pool = collectPool(input.moduleResults ?? []);
  const declaredRoles = collectDeclaredRoles(contract);
  const relationships = input.relationships ?? null;
  const decisionAllowed =
    contract.recommendationPolicy === 'optional' ||
    contract.recommendationPolicy === 'required';

  const namedGaps: AnswerContextNamedGap[] = [];
  const prohibitedSubstitutions: AnswerContextProhibitedSubstitution[] = [];
  const clauseDiagnostics: AnswerContextClauseDiagnostic[] = [];
  const prohibitedSeen = new Set<string>();
  /** Required, non-decision subject keys — the dependency-closure roots. */
  const requiredSubjectKeys: string[] = [];

  // Clause coverage and fact dedupe are SEPARATE concerns (review 5309466440
  // finding 1): every clause admission is RECORDED here, keyed by the exact
  // pool binding; canonical facts and display buckets are derived AFTER the
  // walk, so the artifact cannot depend on obligation declaration order and a
  // later required admission is never discarded by an earlier optional one.
  const admissionsByEntry = new Map<number, AnswerContextAdmission[]>();
  const admit = (entry: PoolEntry, admission: AnswerContextAdmission): void => {
    const existing = admissionsByEntry.get(entry.index) ?? [];
    const duplicate = existing.some(
      (candidate) =>
        candidate.channel === admission.channel &&
        candidate.admittedBy === admission.admittedBy &&
        candidate.metricKey === admission.metricKey,
    );
    if (!duplicate) existing.push(admission);
    admissionsByEntry.set(entry.index, existing);
  };

  const recordGap = (
    obligation: ExecutiveAnswerObligation,
    reason: AnswerContextNamedGapReason,
    metricKeys: readonly string[],
  ): void => {
    namedGaps.push({
      obligationId: safeString(obligation.obligationId).trim(),
      role: obligation.role,
      reason,
      metricKeys,
      permittedGapStatement:
        safeString(obligation.permittedGap?.statement).trim() || null,
    });
  };

  // ---- Clauses 1 + 5a: obligation metric keys (required, optional, decision).
  for (const obligation of contract.obligations ?? []) {
    const obligationId = safeString(obligation?.obligationId).trim();
    const keys = trimmedKeys(obligation?.metricKeys);
    const bindingPlaceholderRefs = trimmedKeys(obligation?.bindingPlaceholderRefs);
    const isDecision = obligation?.role === 'decision';

    if (isDecision && !decisionAllowed) {
      // Decision facts only when recommendationPolicy allows — a decision
      // obligation under 'not_applicable' can never be satisfied; its keys do
      // not enter the subject closure either.
      recordGap(obligation, 'decision_facts_not_permitted_by_recommendation_policy', keys);
      continue;
    }

    if (keys.length === 0) {
      if (obligation?.admissibleBinding) {
        // A role/relationship-only obligation cannot be discharged against a
        // pool that carries no role metadata (module docstring). Diagnose it;
        // when required, its unsatisfiability is a named gap, never silence.
        clauseDiagnostics.push({
          kind: 'admissible_binding_without_metric_keys_no_binding_role_metadata',
          clauseRef: `obligation:${obligationId}`,
        });
        if (obligation.required) {
          recordGap(
            obligation,
            'admissible_binding_unevaluable_no_binding_role_metadata',
            [],
          );
        }
      } else if (obligation?.required) {
        // Key-less, constraint-less obligations are a contract defect the
        // Slice 1 validator rejects; compiled defensively as an ordinary gap.
        recordGap(obligation, 'no_governed_binding_for_metric_keys', []);
      }
      continue;
    }

    const claimedRoleRaw = obligation?.admissibleBinding?.semanticRole;
    const claimedRole = isSemanticNumberRole(claimedRoleRaw) ? claimedRoleRaw : null;
    let satisfied = false;

    for (const key of keys) {
      for (const entry of pool) {
        if (!entryMatchesMetricKey(entry, key)) continue;
        if (!entryMatchesBindingPlaceholderRefs(entry, bindingPlaceholderRefs)) {
          continue;
        }

        if (claimedRole) {
          // Prohibited-substitution fence: a binding whose contract-declared
          // role is forbidden from playing this obligation's claimed role is
          // recorded and does NOT satisfy the obligation. It stays admitted
          // under its own declaring clause — diagnose, never erase.
          const violations = rolesOfferedByEntry(entry, declaredRoles).filter(
            (offered) =>
              offered.declaredBy !== `obligation:${obligationId}` &&
              prohibitedDefectClass(offered.role, claimedRole) !== null,
          );
          if (violations.length > 0) {
            for (const offered of violations) {
              const seenKey = `${entry.index}|${obligationId}|${offered.role}|${claimedRole}`;
              if (prohibitedSeen.has(seenKey)) continue;
              prohibitedSeen.add(seenKey);
              prohibitedSubstitutions.push({
                bindingRef: bindingRefForEntry(entry),
                placeholder: entry.placeholder,
                metricKey: key,
                obligationId,
                offeredRole: offered.role,
                claimedRole,
                defectClass: prohibitedDefectClass(offered.role, claimedRole)!,
                offeredRoleDeclaredBy: offered.declaredBy,
              });
            }
            continue;
          }
        }

        satisfied = true;
        admit(
          entry,
          isDecision
            ? { metricKey: key, channel: 'decision_obligation', admittedBy: obligationId }
            : {
                metricKey: key,
                channel: obligation.required
                  ? 'required_subject_metric'
                  : 'optional_subject_metric',
                admittedBy: obligationId,
              },
        );
      }
    }

    if (!isDecision && obligation.required) requiredSubjectKeys.push(...keys);
    if (!satisfied && obligation.required) {
      recordGap(obligation, 'no_governed_binding_for_metric_keys', keys);
    }
  }

  // ---- Clause 2: direct calculation dependencies of the REQUIRED subject
  // metrics (epic-verbatim), one governed-registry hop, never transitive.
  if (relationships) {
    const seenSubjects = new Set<string>();
    for (const subjectKey of requiredSubjectKeys) {
      if (seenSubjects.has(subjectKey)) continue;
      seenSubjects.add(subjectKey);
      const dependencyKeys =
        relationships[subjectKey]?.directCalculationDependencyKeys ?? [];
      for (const dependencyKey of trimmedKeys(dependencyKeys)) {
        const bindingPlaceholderRefs = bindingPlaceholderRefsForDeclaredMetric(
          contract,
          dependencyKey
        );
        for (const entry of pool) {
          if (!entryMatchesMetricKey(entry, dependencyKey)) continue;
          if (!entryMatchesBindingPlaceholderRefs(entry, bindingPlaceholderRefs)) {
            continue;
          }
          admit(entry, {
            metricKey: dependencyKey,
            channel: 'direct_calculation_dependency',
            admittedBy: `dependency_of:${subjectKey}`,
            subjectMetricKey: subjectKey,
          });
        }
      }
    }
  }

  // ---- Clauses 3 + 4 + 5b: the structured supporting-fact admissions
  // (benchmark/target dependencies ride these — contract-declared only, see
  // the named gap in the module docstring — alongside explanatory facts and
  // policy-gated decision support).
  (contract.permittedSupportingRoles ?? []).forEach(
    (entry: ExecutiveAnswerPermittedSupportingRole, index: number) => {
      const clauseRef = `permittedSupportingRoles[${index}]`;
      const isDecisionSupport = entry?.relationshipType === 'decision_support';
      if (isDecisionSupport && !decisionAllowed) {
        clauseDiagnostics.push({
          kind: 'decision_support_blocked_by_recommendation_policy',
          clauseRef,
        });
        return;
      }
      const keys = trimmedKeys(entry?.metricKeyRefs);
      const bindingPlaceholderRefs = trimmedKeys(entry?.bindingPlaceholderRefs);
      if (keys.length === 0) {
        clauseDiagnostics.push({
          kind: 'supporting_role_without_metric_key_refs_no_binding_role_metadata',
          clauseRef,
        });
        return;
      }
      let grainDiagnosed = false;
      for (const key of keys) {
        for (const poolEntry of pool) {
          if (!entryMatchesMetricKey(poolEntry, key)) continue;
          if (!entryMatchesBindingPlaceholderRefs(poolEntry, bindingPlaceholderRefs)) {
            continue;
          }
          const verdict = scopeConstraintVerdict(entry.scopeConstraint, poolEntry);
          if (verdict === 'unevaluable') {
            if (!grainDiagnosed) {
              grainDiagnosed = true;
              clauseDiagnostics.push({
                kind: 'scope_constraint_grain_unevaluable',
                clauseRef,
              });
            }
            continue;
          }
          if (verdict === 'no_match') continue;
          admit(poolEntry, {
            metricKey: key,
            channel: isDecisionSupport
              ? 'decision_support_role'
              : 'permitted_supporting_role',
            admittedBy: clauseRef,
            relationshipType: entry.relationshipType,
          });
        }
      }
    },
  );

  // ---- Canonicalization (review 5309466440): one fact per admitted binding,
  // carrying EVERY clause admission. The display bucket derives from the full
  // admission set under a deterministic precedence — required_subject_metric
  // beats the decision channels beats everything else — and choosing a bucket
  // never discards the other admissions; a validator proves clause coverage
  // from `admissions` alone, independent of declaration order.
  const requiredFacts: AnswerContextFact[] = [];
  const supportingFacts: AnswerContextFact[] = [];
  const permittedDecisionFacts: AnswerContextFact[] = [];
  for (const entry of pool) {
    const admissions = admissionsByEntry.get(entry.index);
    if (!admissions || admissions.length === 0) continue;
    const fact: AnswerContextFact = {
      bindingRef: bindingRefForEntry(entry),
      placeholder: entry.placeholder,
      moduleId: entry.moduleId,
      admissions,
    };
    const hasRequired = admissions.some(
      (admission) => admission.channel === 'required_subject_metric',
    );
    const hasDecision = admissions.some(
      (admission) =>
        admission.channel === 'decision_obligation' ||
        admission.channel === 'decision_support_role',
    );
    if (hasRequired) requiredFacts.push(fact);
    else if (hasDecision) permittedDecisionFacts.push(fact);
    else supportingFacts.push(fact);
  }

  // ---- Excluded facts: present in today's payload, admitted by nothing.
  // NAMED here for the shadow counters — nothing is dropped anywhere.
  const excludedFacts: AnswerContextExcludedFact[] = pool
    .filter((entry) => !admissionsByEntry.has(entry.index))
    .map((entry) => ({
      bindingRef: bindingRefForEntry(entry),
      placeholder: entry.placeholder,
      moduleId: entry.moduleId,
    }));

  return {
    capabilityId: selection.capabilityId,
    intentId: selection.intentId,
    contractVersion: safeString(contract.contractVersion).trim(),
    requiredObligations,
    requiredFacts,
    supportingFacts,
    permittedDecisionFacts,
    namedGaps,
    excludedFacts,
    prohibitedSubstitutions,
    clauseDiagnostics,
    payloadBindingCount: pool.length,
  };
}

/**
 * GOVERNED direct-calculation-dependency metadata for every metric key the
 * contract's obligations name, read from the compiled governed metric registry
 * — the one governed relationship source the estate has today:
 *
 *   - `composedFrom.inputMetricKeys` — the approved formula's declared inputs
 *     (e.g. ebitda_usd ← operating_income_usd + depreciation_amortization_usd);
 *   - ratio `components` — declared numerator/denominator metric keys
 *     (e.g. ebitda_margin_pct ← ebitda_usd / revenue_usd).
 *
 * Resolution uses `resolveRegisteredMetricAcrossCompiledVersions`, which fails
 * closed on unknown or ambiguous ids — an unresolvable key contributes NO
 * dependencies rather than invented ones. Benchmark/target adjacency does not
 * exist as governed data (module docstring); this builder returns calculation
 * edges only.
 */
export function governedMetricRelationshipsForContract(
  contract: ExecutiveAnswerContract,
): AnswerContextMetricRelationships {
  const relationships: Record<string, AnswerContextMetricRelationship> = {};
  const subjectKeys = new Set<string>();
  for (const obligation of contract.obligations ?? []) {
    for (const key of trimmedKeys(obligation?.metricKeys)) subjectKeys.add(key);
  }
  for (const key of subjectKeys) {
    const entry = resolveRegisteredMetricAcrossCompiledVersions(key);
    if (!entry) continue;
    const dependencyKeys = new Set<string>();
    for (const input of entry.composedFrom?.inputMetricKeys ?? []) {
      const trimmed = safeString(input).trim();
      if (trimmed && trimmed !== key) dependencyKeys.add(trimmed);
    }
    for (const component of [
      ...(entry.components?.numerator ?? []),
      ...(entry.components?.denominator ?? []),
    ]) {
      const trimmed = safeString(component?.metricKey).trim();
      if (trimmed && trimmed !== key) dependencyKeys.add(trimmed);
    }
    if (dependencyKeys.size > 0) {
      relationships[key] = {
        directCalculationDependencyKeys: Object.freeze([...dependencyKeys]),
      };
    }
  }
  return Object.freeze(relationships);
}
